import type { ChannelShareData } from "@expert/sales-tools";

import { getMappedIntent, useCallDetailsStore } from "@expert/sales-tools";

import type { IVRInfo, VoiceTask } from "@/sdk";

import { isVoiceTask, useMostRecentTask } from "@/sdk";

/** Which of the two IVR paths supplied `intent`, or neither. */
export type IntentSource = "contactAttribute" | "channelShareDatabase" | "none";

/**
 * The single source of truth for which IVR path wins. Deliberately returns the source alongside the
 * value: an `out_of_scope` attribute yields no intent yet still suppresses the CSD fallback, so the
 * resolved intent alone cannot tell that apart from nothing having been captured.
 */
export function resolveIntent(
    intentFromVoiceTask: string | undefined,
    intentFromCSD: string | undefined,
): { intent: string | undefined; intentSource: IntentSource } {
    if (intentFromVoiceTask) {
        return { intent: getMappedIntent(intentFromVoiceTask), intentSource: "contactAttribute" };
    }
    if (intentFromCSD) return { intent: intentFromCSD, intentSource: "channelShareDatabase" };
    return { intent: intentFromCSD, intentSource: "none" };
}

export function useIVRInfo() {
    const task = useMostRecentTask();
    const voiceTask = isVoiceTask(task) ? task : null;
    const callDetails = useCallDetailsStore((state) => state.callDetails);
    return getIVRInfo(voiceTask, callDetails);
}

/**
 * The intent and which of the two paths supplied it. Kept off `useIVRInfo` deliberately rather than out
 * of necessity: `intentSource` describes our own resolution, not data the IVR sent, and that object is
 * spread into SalesContext and on through sales-tools. `intent` matches `useIVRInfo().intent` by
 * construction — both resolve the same two inputs through `resolveIntent`.
 */
export function useResolvedIntent(): { intent: string | undefined; intentSource: IntentSource } {
    const task = useMostRecentTask();
    const voiceTask = isVoiceTask(task) ? task : null;
    const callDetails = useCallDetailsStore((state) => state.callDetails);
    return resolveIntent(voiceTask?.ivrInfo?.intent, callDetails.INTENT);
}

function getIVRInfoFromCSD(callDetails: ChannelShareData) {
    const {
        INTENT: intent,
        SUMMARY: summary,
        CONVERSATIONAL_SUMMARY: conversationalSummary,
        CUSTOMER_NAME: customerName,
        ORDER_NUMBER: orderNumber,
        PRODUCT_LINK: productLink,
        PRODUCT_TITLE: productTitle,
        BRAND_PHONE_NUMBER: brandPhoneNumber,
        SUPPORT_PHONE_NUMBER: supportPhoneNumber,
        ACCOUNT_ID: accountId,
    } = callDetails;

    return {
        intent,
        summary,
        customerName,
        orderNumber,
        productLink,
        conversationalSummary,
        productTitle,
        brandPhoneNumber,
        supportPhoneNumber,
        accountId,
    } as IVRInfo;
}

export function getIVRInfo(voiceTask: VoiceTask | null, callDetails?: ChannelShareData) {
    const {
        intent: intentFromVoiceTask,
        summary: summaryFromVoiceTask,
        customerName: customerNameFromVoiceTask,
        orderNumber: orderNumberFromVoiceTask,
        productLink: productLinkFromVoiceTask,
        conversationalSummary: conversationalSummaryFromVoiceTask,
        productTitle: productTitleFromVoiceTask,
        brandPhoneNumber: brandPhoneNumberFromVoiceTask,
        supportPhoneNumber: supportPhoneNumberFromVoiceTask,
        accountId: accountIdFromVoiceTask,
    } = voiceTask?.ivrInfo || {};

    const {
        intent: intentFromCSD,
        summary: summaryFromCSD,
        customerName: customerNameFromCSD,
        orderNumber: orderNumberFromCSD,
        productLink: productLinkFromCSD,
        conversationalSummary: conversationalSummaryFromCSD,
        productTitle: productTitleFromCSD,
        brandPhoneNumber: brandPhoneNumberFromCSD,
        supportPhoneNumber: supportPhoneNumberFromCSD,
        accountId: accountIdFromCSD,
    } = getIVRInfoFromCSD(callDetails ?? useCallDetailsStore.getState().callDetails);

    const { intent } = resolveIntent(intentFromVoiceTask, intentFromCSD);
    const summary = summaryFromVoiceTask ?? summaryFromCSD;
    const conversationalSummary = conversationalSummaryFromVoiceTask ?? conversationalSummaryFromCSD;
    const customerName = customerNameFromVoiceTask ?? customerNameFromCSD;
    const orderNumber = orderNumberFromVoiceTask ?? orderNumberFromCSD;
    const productLink = productLinkFromVoiceTask ?? productLinkFromCSD;
    const productTitle = productTitleFromVoiceTask ?? productTitleFromCSD;
    const brandPhoneNumber = brandPhoneNumberFromVoiceTask ?? brandPhoneNumberFromCSD;
    const supportPhoneNumber = supportPhoneNumberFromVoiceTask ?? supportPhoneNumberFromCSD;
    const accountId = accountIdFromVoiceTask ?? accountIdFromCSD;

    return {
        ...voiceTask?.ivrInfo,
        intent,
        summary,
        customerName,
        orderNumber,
        productLink,
        conversationalSummary,
        productTitle,
        brandPhoneNumber,
        supportPhoneNumber,
        accountId,
    };
}
